import { useId, useRef, useState, type KeyboardEvent } from 'react';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { HelpTooltip } from '../../../shared/components/HelpTooltip';
import { US_STATE_CODES } from '../../people/types';
import { employeeOnboardingGateway } from '../index';
import { EoInput, EoSelect } from '../components/Fields';
import { SectionFrame } from '../components/SectionFrame';
import type { AddressDto } from '../types';
import { EMPTY_ADDRESS, validateAddress, type AddressErrors, type AddressForm } from '../validation';
import type { SectionProps } from './sectionProps';

/** Figma copy for the remote-employee help text (Employee Setup, Home/Residence Address). */
const PRIMARY_WORK_LOCATION_HELP =
  'Select this if the employee primarily works from their residence address, such as in a remote or work-from-home arrangement. The residence address will be used as their primary work location for payroll and tax setup.';

const toForm = (a: AddressDto | null): AddressForm =>
  a ? { line1: a.line1, line2: a.line2 ?? '', city: a.city, state: a.state, postalCode: a.postalCode } : EMPTY_ADDRESS;

const toDto = (f: AddressForm): AddressDto => ({
  line1: f.line1.trim(),
  line2: f.line2.trim() || null,
  country: 'US',
  city: f.city.trim(),
  state: f.state,
  postalCode: f.postalCode.trim(),
});

function AddressFields({ value, errors, onChange }: { value: AddressForm; errors: AddressErrors; onChange: (next: AddressForm) => void }) {
  const set = (patch: Partial<AddressForm>) => onChange({ ...value, ...patch });
  return (
    <div className="eo-grid">
      <EoInput label="Address Line 1" required value={value.line1} maxLength={200} autoComplete="address-line1" error={errors.line1} onChange={(e) => set({ line1: e.target.value })} />
      <EoInput label="Address Line 2" value={value.line2} maxLength={200} autoComplete="address-line2" onChange={(e) => set({ line2: e.target.value })} />
      <EoSelect label="Country" required value="US" disabled>
        <option value="US">United States</option>
      </EoSelect>
      <EoInput label="City" required value={value.city} maxLength={100} autoComplete="address-level2" error={errors.city} onChange={(e) => set({ city: e.target.value })} />
      <EoSelect label="State" required value={value.state} error={errors.state} onChange={(e) => set({ state: e.target.value })}>
        <option value="">Select</option>
        {US_STATE_CODES.map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </EoSelect>
      <EoInput label="Zipcode" required value={value.postalCode} maxLength={10} autoComplete="postal-code" placeholder="12345 or 12345-6789" error={errors.postalCode} onChange={(e) => set({ postalCode: e.target.value })} />
    </div>
  );
}

type Tab = 'residence' | 'mailing';

export function AddressSection({ setup, refresh, next, back }: SectionProps) {
  const [residence, setResidence] = useState<AddressForm>(toForm(setup.residenceAddress));
  const [mailing, setMailing] = useState<AddressForm>(toForm(setup.mailingAddress));
  const [same, setSame] = useState(setup.mailingSameAsResidence);
  const [primary, setPrimary] = useState(setup.residenceIsPrimaryWorkLocation === true);
  const canSetPrimary = setup.workArrangement === 'Remote';
  const [tab, setTab] = useState<Tab>('residence');
  const [residenceErrors, setResidenceErrors] = useState<AddressErrors>({});
  const [mailingErrors, setMailingErrors] = useState<AddressErrors>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const mailingCheckId = useId();
  const tabRefs = useRef<Record<Tab, HTMLButtonElement | null>>({ residence: null, mailing: null });

  function setSameAs(value: boolean) {
    setSame(value);
    // Toggling never discards what was typed (the drafts stay in state) and never leaves a stale error behind.
    setMailingErrors({});
    setResidenceErrors({});
    setError(null);
    setTab('residence');
  }

  function onTabKey(e: KeyboardEvent<HTMLButtonElement>) {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const nextTab: Tab = tab === 'residence' ? 'mailing' : 'residence';
    setTab(nextTab);
    tabRefs.current[nextTab]?.focus();
  }

  async function save() {
    setBusy(true);
    try {
      await employeeOnboardingGateway.saveAddresses({
        residence: toDto(residence),
        mailingSameAsResidence: same,
        ...(same ? {} : { mailing: toDto(mailing) }),
        ...(canSetPrimary ? { residenceIsPrimaryWorkLocation: primary } : {}),
      });
      await refresh();
      next();
    } catch (err) {
      setError(friendlyErrorMessage(err, 'We could not save your address. Please try again.'));
    } finally {
      setBusy(false);
    }
  }

  async function submit() {
    setError(null);
    const re = validateAddress(residence);
    setResidenceErrors(re);
    if (Object.keys(re).length > 0) {
      setTab('residence');
      return;
    }
    if (same) {
      setMailingErrors({});
      await save();
      return;
    }
    const me = validateAddress(mailing);
    if (tab === 'residence') {
      // Residence is fine; the next stop is the Mailing Address panel. Only show its errors once the employee is on it.
      setMailingErrors({});
      if (Object.keys(me).length > 0) {
        setTab('mailing');
        return;
      }
    } else {
      setMailingErrors(me);
      if (Object.keys(me).length > 0) return;
    }
    await save();
  }

  function clearDraft() {
    setResidence(EMPTY_ADDRESS);
    setMailing(EMPTY_ADDRESS);
    setResidenceErrors({});
    setMailingErrors({});
    setError(null);
  }

  const showMailingTab = !same;

  return (
    <SectionFrame title={showMailingTab && tab === 'mailing' ? 'Mailing Address' : 'Residence Address'} onSubmit={submit} onBack={back} onClearDraft={clearDraft} busy={busy} error={error}>
      {showMailingTab && (
        <div className="eo-tabs" role="tablist" aria-label="Address type">
          {(['residence', 'mailing'] as const).map((t) => (
            <button
              key={t}
              ref={(el) => {
                tabRefs.current[t] = el;
              }}
              type="button"
              role="tab"
              id={`eo-tab-${t}`}
              aria-selected={tab === t}
              aria-controls="eo-tabpanel"
              tabIndex={tab === t ? 0 : -1}
              className={`eo-tabs__tab${tab === t ? ' eo-tabs__tab--active' : ''}`}
              onClick={() => setTab(t)}
              onKeyDown={onTabKey}
            >
              {t === 'residence' ? 'Residence Address' : 'Mailing Address'}
            </button>
          ))}
        </div>
      )}

      <div id="eo-tabpanel" role={showMailingTab ? 'tabpanel' : undefined} aria-labelledby={showMailingTab ? `eo-tab-${tab}` : undefined}>
        {tab === 'residence' || !showMailingTab ? (
          <AddressFields value={residence} errors={residenceErrors} onChange={setResidence} />
        ) : (
          <AddressFields value={mailing} errors={mailingErrors} onChange={setMailing} />
        )}
      </div>

      {tab === 'residence' && (
        <>
          <div className="eo-inline-row">
            <label htmlFor={mailingCheckId} className="eo-check">
              <input id={mailingCheckId} type="checkbox" checked={same} onChange={(e) => setSameAs(e.target.checked)} />
              <span>Mailing Address same as Residence Address</span>
            </label>
            {same && (
              <button type="button" className="eo-link-button" onClick={() => setSameAs(false)}>
                + Add Mailing Address
              </button>
            )}
          </div>

          {/* Remote-employee semantics are the existing ones: the residence address can also be the primary work location.
              That flag drives payroll tax, so the employee can change it only while the administrator has set Remote (the server enforces it). */}
          <div className="eo-inline-row">
            <span className="eo-field__label">Primary Work Location</span>
            <label className="eo-check">
              <input type="checkbox" disabled={!canSetPrimary} checked={canSetPrimary ? primary : setup.residenceIsPrimaryWorkLocation === true} onChange={(e) => setPrimary(e.target.checked)} />
              <span>Use Residence Address as Primary Work Location</span>
            </label>
            <HelpTooltip label="About using the residence address as the primary work location" text={PRIMARY_WORK_LOCATION_HELP} />
          </div>
          {!canSetPrimary && <p className="eo-field__hint">Your employer manages your primary work location. Contact your administrator if this needs to change.</p>}
        </>
      )}
    </SectionFrame>
  );
}
